import {
  VStack,
  Flex,
  Heading,
  Text,
  Image,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { DataMetadataContextType } from '../hooks/DataMetadataContextType';
import { ValleyBankDataMedataHeader } from './ValleyBankDataMedataHeader';
import { ValleyBankHeader } from './ValleyBankHeader';
import { ValleyBankSideBar } from './ValleyBankSideBar';
import { errorToastConfig } from 'packages/common-frontend/constants';
import mainLogo from 'packages/common-frontend/images/Logo-square.svg';
import CreditFacilityProvider from 'packages/credit-facility/hooks/CreditFacilityProvider';
import CreditFacilityMissingFallback from 'packages/credit-facility/ui/CreditFacilityMissingFallback';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';

export const ValleyBankPipelineInProgress = ({
  context,
  isAssignmentEnabled,
  isAdvanceRateEnabled,
}: {
  context: DataMetadataContextType;
  isAssignmentEnabled: boolean;
  isAdvanceRateEnabled: boolean;
}) => {
  const toast = useToast();
  const isProduction = useMemo(() => commonUtils.EnvUtils.isProduction(), []);
  const { metadata } = context;
  const progressString =
    metadata && !isProduction ? metadata.details.join(',') : '';

  const [logoLoaded, setLogoLoaded] = useState(false);

  const { data, loading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (!data || loading) {
    return (
      <Skeleton data-testid="loading-capital-provider-router" height="600px" />
    );
  }

  const { user } = data;

  finleyAssert(user, 'User expected to exist');

  return (
    <>
      <ValleyBankHeader />
      <ValleyBankDataMedataHeader context={context} />
      <Flex minH="95vh" grow={1} pt={`${HEADER_HEIGHT}px`}>
        <CreditFacilityProvider
          organizationId={user.organizationId}
          renderLoading={<Skeleton />}
          fallbackComponent={<CreditFacilityMissingFallback />}
        >
          <ValleyBankSideBar
            canToggleDataSource={false}
            isAdvanceRateEnabled={isAdvanceRateEnabled}
            isAssignmentEnabled={isAssignmentEnabled}
          />

          <VStack
            w="100%"
            h="100%"
            flexDir="column"
            p="100px 200px"
            spacing="64px"
          >
            <VStack spacing="16px">
              <Heading as="h1">Check back in a few minutes</Heading>
              <Text>The calculations are updating.</Text>
            </VStack>
            <Image
              src={mainLogo}
              alt="Logo"
              width={{ base: '100px', sm: '140px', md: '180px' }}
              cursor="pointer"
              display={logoLoaded ? 'block' : 'none'}
              h="auto"
              onLoad={() => {
                setLogoLoaded(true);
              }}
            />
            <Text fontSize="sm" color="gray.500">
              {progressString}
            </Text>
          </VStack>
        </CreditFacilityProvider>
      </Flex>
    </>
  );
};
